import React from 'react'

import classNames from 'classnames'

import { LayoutEditorCollapsibleControl } from 'features/views/LayoutEditor/controls/LayoutEditorCollapsibleControl'

import { Box } from 'ui/components/Box'
import { BoxProps } from 'ui/components/Box/Box'
import { Button } from 'ui/components/Button'
import { Icon } from 'ui/components/Icon'
import { Input } from 'ui/components/Input'
import { Body, Headline } from 'ui/components/Text'
import { Tooltip } from 'ui/components/Tooltip'

import { StepIcon } from './StepIcon'
import { NodeGroup, useWorkflowNodeListState } from './useWorkflowNodeListState'

import {
    WorkflowNodeListItemShowOnHoverStyle,
    WorkflowNodeListItemStyle,
} from './WorkflowNodeList.css'

type WorkflowNodeListProps = BoxProps & {}

export const WorkflowNodeList: React.FC<WorkflowNodeListProps> = (props) => {
    const {
        searchQuery,
        onSearchQueryChange,
        canSearch,
        groups,
        addNode,
        isEmpty,
        isEmptySearch,
        isError,
        onClearSearch,
    } = useWorkflowNodeListState()

    return (
        <Box pt="3xl" flex column gap="m" {...props}>
            <Box flex column px="3xl">
                <Headline size="xs">Add Action</Headline>
                <Body size="m" color="textWeaker">
                    Select an action, you want to initiate
                </Body>
                <Input
                    width="full"
                    placeholder="Search..."
                    size="m"
                    mt="xl"
                    value={searchQuery}
                    onChange={onSearchQueryChange}
                    endAction={() => (
                        <Box {...props}>
                            <Button
                                size="xs"
                                variant="ghost"
                                aria-label={!!searchQuery ? 'Clear search' : 'Search'}
                                startIcon={{ name: !!searchQuery ? 'X' : 'Search' }}
                                onClick={!!searchQuery ? onClearSearch : undefined}
                                pointerEvents={!!searchQuery ? undefined : 'none'}
                                tabIndex={!!searchQuery ? undefined : -1}
                            />
                        </Box>
                    )}
                    disabled={!canSearch}
                />
            </Box>
            <Box flex column>
                {(isEmpty || isEmptySearch || isError) && (
                    <Box px="4xl">
                        {isEmpty && (
                            <Body size="m" color="textHelper">
                                No actions found
                            </Body>
                        )}
                        {isEmptySearch && (
                            <Body size="m" color="textHelper">
                                No actions found for your search
                            </Body>
                        )}
                        {isError && (
                            <Body size="m" color="textHelper">
                                Error loading actions. Please try again later.
                            </Body>
                        )}
                    </Box>
                )}

                <WorkflowGroupList groups={groups} addNode={addNode} />
            </Box>
        </Box>
    )
}

type WorkflowGroupListProps = BoxProps & {
    groups: NodeGroup[]
    addNode: (node: WorkflowSchemaNodeType) => void
}

export const WorkflowGroupList: React.FC<WorkflowGroupListProps> = ({
    groups,
    addNode,
    ...props
}) => {
    return (
        <Box {...props}>
            {groups.map((group) => (
                <WorkflowGroupListItem
                    key={group.id}
                    group={group}
                    isOnlyGroup={groups.length === 1}
                    addNode={addNode}
                />
            ))}
        </Box>
    )
}

type WorkflowGroupListItemProps = {
    group: NodeGroup
    isOnlyGroup: boolean
    addNode: (node: WorkflowSchemaNodeType) => void
}

const WorkflowGroupListItem: React.FC<WorkflowGroupListItemProps> = ({
    group,
    isOnlyGroup,
    addNode,
}) => {
    const content = (
        <Box flex column gap="s" px="3xl">
            {group.nodes.map((node) => (
                <WorkflowNodeListItem key={node.id} nodeType={node} onClick={() => addNode(node)} />
            ))}
        </Box>
    )

    // If there is only one group, we don't need to show the collapsible control.
    if (isOnlyGroup) {
        return content
    }

    return (
        <LayoutEditorCollapsibleControl
            key={group.id}
            label={group.name}
            px="3xl"
            py="m"
            contentProps={{
                pl: 0,
                pr: 0,
                pt: 0,
                pb: 'm',
            }}
        >
            {content}
        </LayoutEditorCollapsibleControl>
    )
}

type WorkflowNodeListItemProps = React.ComponentPropsWithoutRef<typeof Button> & {
    nodeType: WorkflowSchemaNodeType
}

const WorkflowNodeListItem: React.FC<WorkflowNodeListItemProps> = ({
    nodeType,
    className,
    ...props
}) => {
    return (
        <Box as="button" className={classNames(WorkflowNodeListItemStyle, className)} {...props}>
            <Box flex center maxWidth="full" grow minWidth={0}>
                <StepIcon nodeType={nodeType} noShrink mr="m" />
                <Box maxWidth="full" minWidth={0} flex center>
                    <Headline size="3xs" textAlign="left" trim maxWidth="full" minWidth={0}>
                        {nodeType.name}
                    </Headline>
                </Box>
                {!!nodeType.description && (
                    <Tooltip
                        content={nodeType.description}
                        zIndex={9999}
                        side="bottom"
                        className={WorkflowNodeListItemShowOnHoverStyle}
                        noShrink
                        ml="xs"
                    >
                        <Icon name="Info" size="m" color="icon" />
                    </Tooltip>
                )}
            </Box>
            <Box className={WorkflowNodeListItemShowOnHoverStyle} pointerEvents="none">
                <Icon name="Plus" size="m" color="icon" />
            </Box>
        </Box>
    )
}
